import { Box, Flex, Text, Image, useToast } from '@chakra-ui/react'
import { ChakraPrimaryButton } from 'components/Button'
import { useLabSchedulingContext } from 'context/LabSchedulingContext/LabSchedulingProvider'
import { Formik, FormikProps } from 'formik'
import * as Yup from 'yup'
import LabCorpLogo from 'assets/images/LabCorpLogo.png'
import { Amplitude } from 'utils/amplitude.utils'
import { LabScheduleZipSelectedProps, LabProvider } from 'utils/amplitude.types'
import AddressAutocompleteV3, {
  FormValues,
} from 'components/AddressAutocomplete/version3'

const FormSchema = Yup.object().shape({
  address: Yup.string()
    .required('Address is required')
    .test('full-address', 'Please enter a full address', function (value) {
      if (!value) {
        return false
      }

      // Check if it's just a ZIP code (5 digits)
      if (/^\d{5}$/.test(value.trim())) {
        return false
      }

      // Check if it's just a ZIP code with city/state (common pattern: "City, State 12345")
      if (/^[^,]*,\s*[A-Z]{2}\s+\d{5}$/.test(value.trim())) {
        return false
      }

      // Check if it's too short to be a full address
      if (value.trim().length < 10) {
        return false
      }

      return true
    }),
})

const AddressSelect = () => {
  const {
    selectedLab,
    setCurrentScreen,
    setLabState,
    isModifyFlow,
    amplitudeLabSchedulingMetadata,
  } = useLabSchedulingContext()
  const toast = useToast()

  const titleText = `Locate your closest ${selectedLab}`

  const submitHandler = async (
    values: FormValues,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    try {
      const lab =
        selectedLab === 'LabCorp' ? LabProvider.LABCORP : LabProvider.QUEST
      const amplitudeData: LabScheduleZipSelectedProps = {
        address: values.address,
        is_reschedule: isModifyFlow,
        source: amplitudeLabSchedulingMetadata.source,
        lab_provider: lab,
        lab_appointment_id:
          amplitudeLabSchedulingMetadata.lab_appointment_id ?? '',
      }

      Amplitude.labScheduleZipSelected(amplitudeData)

      setLabState({ selectedAddress: values.address })
      setCurrentScreen('timeslotMap')
    } catch (error) {
      toast({
        title: 'Error',
        description:
          'Failed to fetch available appointments. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      console.error('Failed to fetch appointments:', error)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Box bg="white" p={8} borderRadius="lg" boxShadow="lg" width="sm">
      <Flex flexDirection="column" alignItems="center" justifyContent="center">
        <Text fontFamily="Instrument Serif" fontSize="1.5rem">
          {titleText}
        </Text>
        <Text>Find a location that&apos;s convenient for you</Text>
        <Formik
          initialValues={{ address: '' }}
          validationSchema={FormSchema}
          onSubmit={submitHandler}
        >
          {(props: FormikProps<FormValues>) => (
            <form onSubmit={props.handleSubmit} style={{ width: '100%' }}>
              <AddressAutocompleteV3
                name="address"
                label="Address"
                placeholder="Enter your address"
              />
              <ChakraPrimaryButton
                type="submit"
                isLoading={props.isSubmitting}
                width="100%"
                disabled={!props.isValid || props.values.address.length === 0}
              >
                Continue
              </ChakraPrimaryButton>
            </form>
          )}
        </Formik>
        {selectedLab === 'LabCorp' && (
          <Image src={LabCorpLogo} width="90px" marginTop="2.5rem" />
        )}
      </Flex>
    </Box>
  )
}

export default AddressSelect
